import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart, Bar, 
  LineChart, Line, 
  XAxis, YAxis, 
  CartesianGrid, Tooltip, 
  ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { 
  Users, Flag, Clock, 
  ArrowUpRight, Activity,
  AlertCircle, RefreshCcw, 
  Pause
} from 'lucide-react';
import { dashboardService, systemHealthService } from '../services/adminServices';
import WaitlistByCityPanel from '../components/users/WaitlistByCityPanel';

function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [refreshInterval, setRefreshInterval] = useState(900); // 15 minutes
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const timeRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '180d', label: 'Last 180 days' },
    { value: '365d', label: 'Last year' }
  ];

  const refreshIntervalOptions = [
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' },
    { value: 900, label: '15 minutes' },
    { value: 1800, label: '30 minutes' },
    { value: 3600, label: '1 hour' }
  ];

  const fetchDashboardData = useCallback(async () => {
    try {
      console.log('[Dashboard] Fetching dashboard data', { timeRange });
      setLoading(true);
      
          // Fetch both dashboard and health data in parallel
      const [dashboardData, healthData] = await Promise.all([
        dashboardService.getDashboardData(timeRange),
        systemHealthService.getSystemHealth()
      ]);

      
      console.log('[Dashboard] Data received:', {
        timeRange,
        metadata: dashboardData.metadata,
        sections: Object.keys(dashboardData),
        health: healthData
      });
      
      setData({
        ...dashboardData,
        system: healthData.system
      });
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('[Dashboard] Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);
  

  // Initial load and time range change effect
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, timeRange]);

  // Auto-refresh effect
  useEffect(() => {
    if (!isAutoRefreshEnabled) return;

    console.log('[Dashboard] Setting up auto-refresh:', { 
      interval: refreshInterval,
      enabled: isAutoRefreshEnabled 
    });

    const intervalId = setInterval(fetchDashboardData, refreshInterval * 1000);
    return () => clearInterval(intervalId);
  }, [fetchDashboardData, refreshInterval, isAutoRefreshEnabled]);

  // Format the last updated time
  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    }).format(lastUpdated);
  };

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <h3 className="ml-2 text-red-800">Error loading dashboard</h3>
        </div>
        <p className="mt-2 text-red-700">{error}</p>
        <button 
          onClick={fetchDashboardData}
          className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Controls section component
  const DashboardControls = () => (
    <div className="flex flex-wrap items-center gap-4 mb-6 bg-white p-4 rounded-lg shadow">
      {/* Time Range Selector */}
      <div className="flex items-center">
        <label htmlFor="timeRange" className="mr-2 text-sm font-medium text-gray-700">
          Time Range:
        </label>
        <select
          id="timeRange"
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          {timeRangeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Refresh Interval Selector */}
      <div className="flex items-center">
        <label htmlFor="refreshInterval" className="mr-2 text-sm font-medium text-gray-700">
          Refresh Every:
        </label>
        <select
          id="refreshInterval"
          value={refreshInterval}
          onChange={(e) => setRefreshInterval(Number(e.target.value))}
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          {refreshIntervalOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Auto-refresh Toggle */}
      <button
        onClick={() => setIsAutoRefreshEnabled(!isAutoRefreshEnabled)}
        className={`flex items-center gap-2 px-3 py-1 rounded-md ${
          isAutoRefreshEnabled 
            ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' 
            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
        }`}
      >
        {isAutoRefreshEnabled ? (
          <>
            <RefreshCcw className="h-4 w-4" />
            Auto-refresh On
          </>
        ) : (
          <>
            <Pause className="h-4 w-4" />
            Auto-refresh Off
          </>
        )}
      </button>

      {/* Manual Refresh Button */}
      <button
        onClick={fetchDashboardData}
        className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
        disabled={loading}
      >
        <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        Refresh Now
      </button>

      {/* Last Updated Timestamp */}
      {lastUpdated && (
        <span className="text-sm text-gray-500">
          Last updated: {formatLastUpdated()}
        </span>
      )}
    </div>
  );

  if (!data) return null;

  // Prepare trend data for charts
  const userTrendData = Object.entries(data.users.trends).map(([date, count]) => ({
    date,
    users: count
  }));

  const retentionData = data.users.retention.map(cohort => ({
    date: cohort.date,
    rate: parseFloat(cohort.rate)
  }));

  const contentTrendData = Object.entries(data.content.trends).map(([date, stats]) => ({
    date,
    content: stats.count,
    engagement: parseFloat((stats.engagement * 100).toFixed(1))
  }));

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <button className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Generate Report
        </button>
      </div>

      {/* Dashboard Controls */}
      <DashboardControls />

      {loading && !data && (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        </div>
      )}

      {data && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {/* Users Card */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl shadow-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Total Users</p>
                  <h3 className="text-3xl font-extrabold text-gray-900">
                    {data.users.current.total.toLocaleString()}
                  </h3>
                </div>
                <div className="p-3 bg-blue-200 rounded-full">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <span className="text-gray-600">
                  {data.users.current.active.toLocaleString()} active
                </span>
              </div>
            </div>

            {/* Waitlist Card */}
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-6 rounded-xl shadow-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Waitlist Users</p>
                  <h3 className="text-3xl font-extrabold text-gray-900">
                    {data.waitlist.total.toLocaleString()}
                  </h3>
                </div>
                <div className="p-3 bg-yellow-200 rounded-full">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <Activity className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 ml-2">
                  {data.waitlist.lastDayJoined} new today
                </span>
              </div>
            </div>

            {/* Reports Card */}
            <div className="bg-gradient-to-r from-red-50 to-red-100 p-6 rounded-xl shadow-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Pending Reports</p>
                  <h3 className="text-3xl font-extrabold text-gray-900">
                    {data.reports.pending}
                  </h3>
                </div>
                <div className="p-3 bg-red-200 rounded-full">
                  <Flag className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <span className="text-gray-600">
                  {data.reports.total} total reports
                </span>
              </div>
            </div>

            {/* Content Growth Card */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl shadow-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Content</p>
                  <h3 className="text-3xl font-extrabold text-gray-900">
                    {data.content.current.total.toLocaleString()}
                  </h3>
                </div>
                <div className="p-3 bg-green-200 rounded-full">
                  <ArrowUpRight className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <span className="text-gray-600">
                  {(data.content.current.avgEngagement * 100).toFixed(1)}% avg engagement
                </span>
              </div>
            </div>
          </div>

          <div className="mt-8">
                <h2 className="text-xl font-bold mb-4">Waitlist by City</h2>
                <WaitlistByCityPanel />
            </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* User Growth Chart */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold mb-4">User Growth</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={userTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="users" 
                      stroke="#2563eb" 
                      fill="#bfdbfe" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Retention Chart */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold mb-4">User Retention</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={retentionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="rate" 
                      stroke="#2563eb" 
                      strokeWidth={2} 
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Content Growth Chart */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Content Growth</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={contentTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="content" fill="#2563eb" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* System Health */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold mb-4">System Health</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Response Time</span>
                  <span className="font-semibold">
                    {data.system.responseTime}ms
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Error Rate</span>
                  <span className="font-semibold">
                    {(data.system.errorRate * 100).toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">System Uptime</span>
                  <span className="font-semibold">
                    {data.system.systemUptime}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">API Errors (24h)</span>
                  <span className="font-semibold">
                    {data.system.apiErrors.last24h}
                    <span className="text-sm text-gray-500 ml-2">
                      ({data.system.apiErrors.trend > 0 ? '+' : ''}{data.system.apiErrors.trend}%)
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
    </div>
  );
}

export default DashboardPage;